import * as React from 'react';
import "./EventCalendar.css"
import { Calendar, dateFnsLocalizer } from "react-big-calendar";
import { format, parse, startOfWeek, getDay } from "date-fns";
import enGB from "date-fns/locale/en-GB";
import "react-big-calendar/lib/css/react-big-calendar.css";
import CalendarToolbar from "../CalendarToolbar/CalendarToolbar";
import { GlobalContext } from "../../Context/GlobalContext";
import CalendarEvent from '../CalendarEvent/CalendarEvent';
import dayjs from 'dayjs';
import { Typography, Box, InputAdornment } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DATE_FORMAT } from '../../Constants/Global';

const locales = { "en-GB": enGB };
const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek: () => startOfWeek(new Date(), { weekStartsOn: 1 }),
  getDay,
  locales,
});

/**
 * Configuration for calendar date display formats across different views.
 */
const formats = {
  // Format for the day names in the month view header (Mon, Tue, Wed)
  weekdayFormat: (date, culture, localizer) =>
    localizer.format(date, 'eee', culture),

  // Format for the day column headers in week view (Mon 01/01)
  dayFormat: (date, culture, localizer) =>
    localizer.format(date, 'eee dd/MM', culture),

  // Format for the header in day view (Monday 01 January)
  dayHeaderFormat: (date, culture, localizer) =>
    localizer.format(date, 'EEEE dd MMMM', culture),

  // Format for the time gutter (vertical axis)
  timeGutterFormat: (date, culture, localizer) =>
    localizer.format(date, 'HH:mm', culture),
};

/**
 * A calendar component that displays events (tasks) in a monthly, weekly, or daily view.
 * Users can select slots to create new events, navigate through dates via a custom
 * DatePicker or navigation buttons, and switch between views.
 * The calendar integrates with the global context to manage events, user permissions, and UI state.
 * * @component
 * @returns {JSX.Element} The rendered calendar component.
 * @example
 * <EventCalendar />
 */
const EventCalendar = () => {

  const {
    setSelectTaskOpen,
    setEventDate,
    currentTab,
    myTasks,
    allTasks,
    currentDate,
    setCurrentDate,
    currentView,
    setCurrentView,
    userProperties,
    setAlertType,
    setAlertTitle,
    setAlertMessage,
    setAlertOpen,
    setAlertBarSeverity,
    setAlertBarMessage,
    setAlertBarOpen
  } = React.useContext(GlobalContext);

  const [events, setEvents] = React.useState([]);
  const [open, setOpen] = React.useState(false); // State to control the picker visibility

  /**
   * Updates the events displayed in the calendar based on the current tab (My Tasks or All Tasks).
   */
  React.useEffect(() => {
    if (currentTab === 1) {
      setEvents(allTasks);
    } else {
      setEvents(myTasks);
    }
  }, [currentTab, allTasks, myTasks]);

  /**
   * Handles the selection of a calendar slot (date/time range). If the user has permission,
   * it opens the task creation form. Otherwise, it displays a permission denied alert.
   *
   * @param {Object} slotInfo - Information about the selected slot, including the start and end times.
   */
  const handleSelectSlot = (slotInfo) => {
    if (!userProperties){
      setAlertBarSeverity("error");
      setAlertBarMessage("userProperties error");
      setAlertBarOpen(true);
    } else if (userProperties.createProcesses){
      setEventDate(dayjs(new Date(slotInfo.start)).format(DATE_FORMAT));
      setSelectTaskOpen(true);
    } else {
      setAlertType("permission_denied");
      setAlertTitle(
        <Typography color='error'>
          <WarningAmberIcon color="error" sx={{ position: "relative", top: "5px", marginRight: "10px" }} />
          Permission denied
        </Typography>
      );
      setAlertMessage(<Typography>You don't have permission to create processes</Typography>);
      // setModalOpen(false);
      setAlertOpen(true);
    }
  };

  /**
   * Handles navigation through the calendar (e.g., changing the displayed month, week, or day).
   *
   * @param {Date} newDate - The new date to navigate to.
   */
  const handleNavigate = (newDate) => {
    setCurrentDate(newDate);
  };

  /**
   * Handles changes to the calendar view (e.g., switching between month, week, and day views).
   *
   * @param {string} newView - The new view to display (e.g., "month", "week", "day").
   */
  const handleViewChange = (newView) => {
    setCurrentView(newView);
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box sx={{ height: "86vh"}}>
        {/* Compact Year/Month Selector with leading Icon and fixed selection bug */}
        <DatePicker
          open={open}
          onOpen={() => setOpen(true)}
          onClose={() => setOpen(false)}
          views={['year', 'month']}
          value={dayjs(currentDate)}
          // onChange update the state, but we don't close here to allow year-then-month flow
          onChange={(newValue) => setCurrentDate(newValue.toDate())}
          // onAccept is triggered only when the selection flow is completed
          onAccept={() => setOpen(false)}
          slotProps={{
            textField: {
              variant: 'standard',
              onClick: () => setOpen(true),
              InputProps: {
                disableUnderline: true,
                startAdornment: (
                  <InputAdornment position="start" sx={{ mr: 0.5, cursor: 'pointer' }}>
                    <ExpandMoreIcon sx={{ color: 'rgba(0, 0, 0, 0.4)' }} />
                  </InputAdornment>
                ),
                sx: {
                    fontSize: '1.5rem',
                    color: 'rgba(0, 0, 0, 0.6)',
                    cursor: 'pointer',
                    width: 'auto',
                    flexDirection: 'row',
                    '& .MuiInputBase-input': {
                        cursor: 'pointer',
                        width: 'fit-content',
                        p: 0,
                        readOnly: true
                    }
                }
              },
            },
            openPickerButton: {
              sx: { display: 'none' }
            }
          }}
          sx={{ mb: 2 }}
        />

        <Calendar
          localizer={localizer}
          events={events}
          startAccessor="start"
          endAccessor="end"
          selectable
          onSelectSlot={handleSelectSlot}
          views={["month", "week", "day"]}
          view={currentView}
          date={currentDate}
          onNavigate={handleNavigate}
          onView={handleViewChange}
          style={{ height: "100%" }}
          formats={formats}
          culture='en-GB'
          components={{
            toolbar: CalendarToolbar,
            event: CalendarEvent
          }}
        />
      </Box>
    </LocalizationProvider>
  );
};

export default EventCalendar;
